import { FC } from "react";

import { Box, Stack, useTheme } from "@mui/material";

import { BaseStep1LayoutProps } from "./BaseStep1LayoutProps";

import { FormRegisteredCharityStatement } from "@/canvas/features/FormV2/blocks/shell/FormRegisteredCharityStatement";
import { ThemedZeffyLogo } from "@/canvas/layouts/ThemedBranding/ThemedZeffyLogo";
import { useHasValueChanged } from "@/common/hooks/useHasValueChanged";
import { useMediaQuery } from "@/common/hooks/useMediaQuery";

export const ONE_COLUMN_MAX_WIDTH = 597;

type BaseStep1OneColumnLayoutProps = Pick<
  BaseStep1LayoutProps,
  "slots" | "showCheckoutFooter" | "contextDrawerHeight" | "logoHref" | "onLogoClick"
>;

export const BaseStep1OneColumnLayout: FC<BaseStep1OneColumnLayoutProps> = ({
  slots,
  showCheckoutFooter = false,
  contextDrawerHeight,
  logoHref,
  onLogoClick,
}) => {
  const theme = useTheme();
  const { isSmallScreen } = useMediaQuery();
  const hasWelcomeBackBanner = Boolean(slots.welcomeBackBanner);
  const [animateCheckoutFooter, watchedShowCheckoutFooter] = useHasValueChanged(showCheckoutFooter);

  const renderAnimatedCheckoutFooter = () => {
    const hidden = !watchedShowCheckoutFooter && !animateCheckoutFooter;

    return (
      <Box
        data-test="form-v2-checkout-footer"
        position={{ xs: "fixed", md: "sticky" }}
        bottom={{ xs: 0, md: theme.spacing(5) }}
        left={0}
        right={0}
        marginBlockStart={{ md: hidden ? 0 : 2 }}
        padding={{ xs: hidden ? 0 : 2, md: 0 }}
        sx={{
          animation: animateCheckoutFooter
            ? watchedShowCheckoutFooter
              ? "fade-slide-in 0.3s ease-in-out both"
              : "fade-slide-out 0.3s ease-in-out both"
            : undefined,
          opacity: hidden ? 0 : undefined,
          height: hidden ? 0 : undefined,
          zIndex: 1000,
          ["@keyframes fade-slide-in"]: {
            from: { translate: "0 20px", opacity: 0 },
            to: { translate: "0 0", opacity: 1 },
          },
          ["@keyframes fade-slide-out"]: {
            from: { translate: "0 0", opacity: 1 },
            to: { translate: "0 20px", opacity: 0 },
          },
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: ONE_COLUMN_MAX_WIDTH,
            marginInline: "auto",
            paddingInline: { xs: 2, md: 0 },

            boxSizing: "border-box",
          }}
        >
          {slots.checkoutFooter}
        </Box>
      </Box>
    );
  };

  return (
    <Stack>
      {slots.welcomeBackBanner && (
        <Box sx={{ position: { xs: "sticky", md: "static" }, top: 0, zIndex: { xs: 50, md: "auto" } }}>
          {slots.welcomeBackBanner}
        </Box>
      )}
      <Stack width="100%" maxWidth={ONE_COLUMN_MAX_WIDTH} marginInline="auto" sx={{ boxSizing: "border-box" }}>
        <Stack
          justifyContent={{ xs: "flex-start", md: "center" }}
          padding={theme.spacing(isSmallScreen ? 2 : hasWelcomeBackBanner ? 6 : 10, 2, 0, 2)}
          sx={{
            boxSizing: "border-box",
            paddingBottom: isSmallScreen ? 0 : undefined,
            gap: isSmallScreen ? 2 : 0,
          }}
        >
          {slots.info}
          <Stack width="100%">
            {slots.order}
            {!isSmallScreen && renderAnimatedCheckoutFooter()}
          </Stack>
          <FormRegisteredCharityStatement />
        </Stack>
      </Stack>
      <Box
        sx={{
          position: { md: "fixed" },
          top: { md: 24 },
          left: { md: 24 },
          display: { xs: "flex", md: "block" },
          justifyContent: { xs: "center", md: "unset" },
          paddingBlock: { xs: 2, md: 0 },
          zIndex: 1,
        }}
      >
        <ThemedZeffyLogo href={logoHref} onClick={onLogoClick} />
      </Box>
      {slots.shopCartButton && (
        <Box
          sx={{
            position: "fixed",
            top: 24 + contextDrawerHeight,
            right: 24,
            zIndex: 1001,
            transition: "top 0.3s ease-out",
          }}
        >
          {slots.shopCartButton}
        </Box>
      )}
      {isSmallScreen && (
        <>
          {watchedShowCheckoutFooter && <Box sx={{ height: 75, visibility: "hidden" }} />}
          {renderAnimatedCheckoutFooter()}
          {slots.mobileChooseTicketsFloatingButton}
        </>
      )}
    </Stack>
  );
};
